import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../stores/api";

export type Slot = {
	start: string;
	end: string;
};

export type SlotsByDay = {
	date: string;
	slots: Slot[];
};

type CampaignAvailabilityResponse = {
	advisor_id: number;
	advisor_name: string;
	calendar_id: string;
	timezone: string;
	slots: Slot[];
	total: number;
	page: number;
	page_size: number;
};

type AvailabilityData = {
	days: SlotsByDay[];
	advisorId: number;
	advisorName: string;
	hasMore: boolean;
};

function ymdInTimezone(d: Date, tz: string): string {
	const parts = new Intl.DateTimeFormat("en-CA", {
		timeZone: tz,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	}).formatToParts(d);
	const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
	return `${get("year")}-${get("month")}-${get("day")}`;
}

function groupSlotsByDay(slots: Slot[], timezone: string): SlotsByDay[] {
	const map = new Map<string, Slot[]>();
	for (const slot of slots) {
		const date = ymdInTimezone(new Date(slot.start), timezone);
		const bucket = map.get(date);
		if (bucket) bucket.push(slot);
		else map.set(date, [slot]);
	}
	return Array.from(map.entries())
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([date, s]) => ({ date, slots: s }));
}

async function fetchCampaignAvailability(
	campaignId: string,
	timezone: string,
	page: number,
): Promise<CampaignAvailabilityResponse> {
	const qs = new URLSearchParams({
		timezone,
		page: String(page),
		page_size: "7",
	});
	return apiFetch("GET", `/voice/campaign/${campaignId}/availability?${qs}`);
}

export function useCampaignAvailability(
	campaignId: string,
	timezone: string,
	page: number,
) {
	return useQuery({
		queryKey: ["campaign-availability", campaignId, timezone, page],
		queryFn: () => fetchCampaignAvailability(campaignId, timezone, page),
		select: (res): AvailabilityData => ({
			days: groupSlotsByDay(res.slots, timezone),
			advisorId: res.advisor_id,
			advisorName: res.advisor_name,
			hasMore: res.page * res.page_size < res.total,
		}),
	});
}
